import React from 'react';
import { datejs } from '@block/utils';
import { CertificateType } from '../../types';
import { urlTypeMap } from '../../consts';
import { EDUCATION_LEVEL, REASON_INDEX, REASON_TYPE_MAP } from './constants';
import { CertificateData } from './types';

interface ReasonTipsProps {
  formState: CertificateData;
  certificateType: CertificateType;
  reasonList: any[];
  data: any;
  openImagePreview: (imageUrl: string, title: string) => void;
}

// 固定的提示链接
const FIXED_TIPS = {
  [CertificateType.ForeignAuth]: {
    url: 'https://zwfw.cscse.edu.cn/cscse/lxfwzxwsfwdt2020/xlxwrz32/index.html',
    text: 'Tips：国（境）外大学毕业的同学需上传《教育部留学服务中心学历学位认证书》',
  },
  [CertificateType.OnlineVerification]: {
    url: 'https://s3plus-bj02.sankuai.com/ssc-default-bucket/onboarding-edu-picture/%E5%AD%A6%E7%B1%8D%E5%9C%A8%E7%BA%BF%E9%AA%8C%E8%AF%81%E6%8A%A5%E5%91%8A.png',
    text: 'Tips：如何获取《教育部学籍在线验证报告》',
    isImage: true,
  },
};

export const ReasonTips: React.FC<ReasonTipsProps> = ({
  formState,
  certificateType,
  reasonList,
  data,
  openImagePreview,
}) => {
  const { unableUpload } = formState;

  // 没有勾选无法上传时，显示固定提示
  if (!unableUpload) {
    const fixedTip = FIXED_TIPS[certificateType];
    if (!fixedTip) return null;

    // 如果是图片类型的提示
    if (fixedTip.isImage) {
      return (
        <div className="reason-tips">
          <span className="clickable-text" onClick={() => openImagePreview(fixedTip.url, fixedTip.text)}>
            {fixedTip.text}
          </span>
        </div>
      );
    }

    // 如果是链接类型的提示
    return (
      <div className="reason-tips">
        <a href={fixedTip.url} target="_blank" rel="noopener noreferrer">
          {fixedTip.text}
        </a>
      </div>
    );
  }

  // 从reasonList中查找当前选择的原因
  const reasonIndex = formState?.reason?.reasonIndex;
  if (reasonIndex === undefined) return null;

  // 毕业证类型
  const isDiploma = certificateType === CertificateType.Diploma;
  // 选择了 毕业证书丢失
  const isDiplomaLoss = reasonIndex === REASON_INDEX.DIPLOMA_LOSS;
  // 特殊情况 高中/中专及以下或毕业时间早于2001年(会实时变动，所以需要取data)
  const isSpecial =
    data?.diplomaDictIndex === EDUCATION_LEVEL.HIGH_SCHOOL ||
    (data?.diplomaDictIndex !== EDUCATION_LEVEL.HIGH_SCHOOL && data?.endDate < datejs('2001-1-1').valueOf());

  // 如果是 毕业证类型 且 毕业证书丢失 且 高中/中专及以下或毕业时间早于2001年 ，不展示Tip
  if (isDiploma && isDiplomaLoss && isSpecial) {
    return null;
  }

  // reasonIndex和item.reasonIndex都应该是number类型，不需要额外转换
  const selectedReason = reasonList.find(
    (item) => item.reasonIndex === reasonIndex && item.reasonType === REASON_TYPE_MAP[certificateType],
  );

  if (!selectedReason) return null;

  const { urlType, url, tipsName } = selectedReason;

  // 根据urlType处理不同的提示方式
  // 图片类型，显示查看图片链接，使用Modal展示图片
  if (urlType === urlTypeMap.image && tipsName) {
    return (
      <div className="reason-tips">
        <span className="clickable-text" onClick={() => openImagePreview(url, tipsName)}>
          {tipsName}
        </span>
      </div>
    );
  }

  // 链接类型，显示链接
  if (urlType === urlTypeMap.link && tipsName) {
    return (
      <div className="reason-tips">
        <a href={url} target="_blank" rel="noopener noreferrer">
          {tipsName}
        </a>
      </div>
    );
  }

  // 纯tips类型，显示提示文本
  if (urlType === urlTypeMap.text && tipsName) {
    return <div className="reason-tips">{tipsName}</div>;
  }

  return null;
};
